/* eslint-disable no-alert */
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiLayout, FiUsers, FiSmartphone, FiMonitor, FiCheck, FiPlay, FiArrowUpRight, FiSearch, FiCheckSquare, FiPenTool, FiCode, FiSend } from 'react-icons/fi';
import { Helmet } from "react-helmet";
import { useState, useRef } from 'react';

const UIDesignPage = () => {
    const [activeService, setActiveService] = useState(0);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const videoRef = useRef(null);

    const services = [
        {
            title: "User Research & Analysis",
            description: "We uncover actionable insights through comprehensive user research methods including interviews, surveys, and analytics.",
            icon: <FiUsers className="text-3xl" />,
            color: "from-purple-500 to-pink-500",
            features: [
                "User interviews and surveys",
                "Competitive analysis",
                "User persona development",
                "Journey mapping",
                "Usability testing"
            ]
        },
        {
            title: "Interface Design",
            description: "Pixel-perfect UI designs that balance aesthetics with functionality for seamless user experiences.",
            icon: <FiLayout className="text-3xl" />,
            color: "from-blue-500 to-cyan-500",
            features: [
                "Visual design systems",
                "Interactive components",
                "Accessibility compliance",
                "Design consistency",
                "Responsive layouts"
            ]
        },
        {
            title: "Prototyping",
            description: "Interactive prototypes that bring your product to life before development begins.",
            icon: <FiMonitor className="text-3xl" />,
            color: "from-green-500 to-teal-500",
            features: [
                "High-fidelity prototypes",
                "Micro-interactions",
                "User flow validation",
                "Stakeholder feedback",
                "Developer handoff"
            ]
        },
        {
            title: "Mobile UX",
            description: "Thumb-friendly mobile experiences designed for engagement and conversions.",
            icon: <FiSmartphone className="text-3xl" />,
            color: "from-orange-500 to-red-500",
            features: [
                "Mobile-first approach",
                "Touch-friendly interfaces",
                "iOS & Android guidelines",
                "Performance optimization",
                "App store compliance"
            ]
        },
        {
            title: "Design Systems",
            description: "Scalable component libraries and style guides for consistent brand experiences.",
            icon: <FiCode className="text-3xl" />,
            color: "from-indigo-500 to-purple-500",
            features: [
                "Component libraries",
                "Design tokens",
                "Documentation",
                "Version control",
                "Team collaboration"
            ]
        }
    ];
    const faqItems = [
        {
            question: "How long does a typical UI/UX design project take?",
            answer: "The timeline varies based on project complexity, but most projects take between 4-8 weeks. We'll provide a detailed timeline after understanding your specific requirements."
        },
        {
            question: "Do you work with startups or only established businesses?",
            answer: "We work with businesses of all sizes, from startups to Fortune 500 companies. We tailor our approach to meet your specific needs and budget."
        },
        {
            question: "What design tools do you use?",
            answer: "We use industry-standard tools including Figma, Sketch, Adobe XD, and InVision for design, along with prototyping tools like Principle and Framer."
        },
        {
            question: "Can you help with redesigning an existing product?",
            answer: "Absolutely! We often help businesses improve existing products through UX audits, redesigns, and usability improvements."
        }
    ];
    const processTimeline = [
        {
            year: "Step 1",
            event: "Discover",
            description: "We conduct in-depth research to understand your users, business goals, and market landscape through interviews, surveys, and analytics.",
            icon: <FiSearch className="text-white" />
        },
        {
            year: "Step 2",
            event: "Define",
            description: "We synthesize research findings to define clear user personas, journey maps, and project requirements that align with business objectives.",
            icon: <FiCheckSquare className="text-white" />
        },
        {
            year: "Step 3",
            event: "Design",
            description: "Our designers create intuitive wireframes, interactive prototypes, and pixel-perfect UI designs focused on user experience and conversion.",
            icon: <FiPenTool className="text-white" />
        },
        {
            year: "Step 4",
            event: "Develop",
            description: "We collaborate with developers to ensure precise implementation, conduct usability testing, and refine based on real user feedback.",
            icon: <FiCode className="text-white" />
        },
        {
            year: "Step 5",
            event: "Deliver",
            description: "We assist with product launch, establish metrics for success, and provide ongoing optimization based on performance data.",
            icon: <FiSend className="text-white" />
        }
    ];

    const testimonials = [
        {
            name: "Alex Johnson",
            role: "Product Manager, TechStart",
            content: "The UI redesign increased our conversion rate by 42% and reduced bounce rate significantly. The attention to detail was exceptional.",
            avatar: "AJ"
        },
        {
            name: "Maria Rodriguez",
            role: "CEO, FashionHub",
            content: "Working with this team transformed our mobile app experience. Our user retention improved by 65% in just three months.",
            avatar: "MR"
        },
        {
            name: "James Wilson",
            role: "CTO, FinTech Solutions",
            content: "The design system they created has saved us countless hours in development and ensured consistency across all our products.",
            avatar: "JW"
        }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Form submission logic would go here
        alert('Thank you for your inquiry! We will contact you soon.');
        setFormData({ name: '', email: '', message: '' });
    };

    const toggleVideoPlay = () => {
        setIsVideoPlaying(!isVideoPlaying);
    };

    return (
        <div className="bg-white">
            <Helmet>
                <title>UI/UX Design Services | Creative & User-Centric Designs</title>
                <meta
                    name="description"
                    content="UniTechnoStack delivers visually stunning and user-focused UI/UX design for websites, mobile apps, and software to boost engagement and user satisfaction."
                />
                <meta
                    name="keywords"
                    content="ui design services, ux design services, user experience design, app design, website design, UniTechnoStack"
                />
                <meta name="robots" content="index, follow" />
                <link
                    rel="canonical"
                    href="https://www.unitechnostack.com/uiux"
                />
                <meta
                    property="og:title"
                    content="UI/UX Design Services | Creative & User-Centric Designs"
                />
                <meta
                    property="og:description"
                    content="We craft user-focused UI/UX designs that improve engagement and enhance customer experiences for apps, websites, and software."
                />
                <meta
                    property="og:url"
                    content="https://www.unitechnostack.com/ui-ux-design"
                />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 to-blue-900 text-white">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-500/10 to-purple-500/10"></div>
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-10 animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mt-10"
                    >
                        <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 leading-tight">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                                Transformative UI/UX Design
                            </span><br />
                            That Drives Engagement
                        </h1>
                        <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-10">
                            We craft intuitive, beautiful digital experiences that increase user satisfaction and boost conversions for businesses worldwide.
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <a href="/quickenquiry">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold hover:shadow-xl transition-all shadow-lg flex items-center"
                                >
                                    Get Your Free Audit
                                    <FiArrowRight className="ml-3" />
                                </motion.button>
                            </a>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
                                onClick={toggleVideoPlay}
                                href="#vid"
                            >
                                Watch Demo
                                <FiPlay className="ml-3 inline" />
                            </motion.button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="mt-10 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-2 shadow-2xl"
                    >
                        <div id="vid" className="aspect-video bg-gradient-to-br from-blue-900 to-purple-900 rounded-xl flex items-center justify-center relative overflow-hidden">
                            {!isVideoPlaying ? (
                                <>
                                    <div className="absolute inset-0 bg-grid-white/5"></div>
                                    <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white flex items-center justify-center hover:shadow-xl shadow-lg z-10"
                                        onClick={toggleVideoPlay}
                                    >
                                        <FiPlay className="text-2xl ml-1" />
                                    </motion.button>
                                </>
                            ) : (
                                <div className="w-full h-full bg-black flex items-center justify-center">
                                    <div className="text-white p-8 text-center">
                                        <div className="text-2xl font-bold mb-2">Video Presentation</div>
                                        <p className="text-blue-200 mb-4">This would be a demo video showcasing our design process</p>
                                        <button
                                            className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium"
                                            onClick={toggleVideoPlay}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { value: "42%", label: "Avg. Conversion Increase" },
                            { value: "95%", label: "Client Satisfaction" },
                            { value: "50+", label: "Projects Completed" },
                            { value: "200%", label: "ROI on Average" }
                        ].map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-md transition-shadow"
                            >
                                <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                                <div className="text-gray-600 text-sm">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Showcase */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-3xl md:text-4xl font-bold mb-4"
                    >
                        Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">
                            UI/UX Design Services
                        </span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        viewport={{ once: true }}
                        className="text-gray-600 max-w-3xl mx-auto text-lg"
                    >
                        Comprehensive solutions tailored to your business goals and user needs
                    </motion.p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
                    {services.map((service, index) => (
                        <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            onClick={() => setActiveService(index)}
                            className={`py-4 px-2 rounded-xl transition-all ${activeService === index ?
                                'bg-gradient-to-br from-gray-900 to-blue-900 text-white shadow-lg' :
                                'bg-gray-100 hover:bg-gray-200'}`}
                        >
                            <div className="flex flex-col items-center">
                                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center text-white mb-2`}>
                                    {service.icon}
                                </div>
                                <span className="font-medium text-xs md:text-sm text-center">{service.title.split(" ")[0]}</span>
                            </div>
                        </motion.button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeService}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden border border-gray-200"
                    >
                        <div className="md:flex">
                            <div className="md:w-1/2 p-8 md:p-12">
                                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${services[activeService].color} flex items-center justify-center text-white mb-6`}>
                                    {services[activeService].icon}
                                </div>
                                <h3 className="text-2xl font-bold mb-4">{services[activeService].title}</h3>
                                <p className="text-gray-700 mb-6">{services[activeService].description}</p>
                                <ul className="space-y-3 mb-8">
                                    {services[activeService].features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <FiCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                                            <span className="text-gray-700">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                
                            </div>
                            <div className="md:w-1/2 bg-gray-100 flex items-center justify-center p-8">
                                <div className="relative w-full h-64 md:h-96 rounded-xl overflow-hidden">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${services[activeService].color} opacity-10`} />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-5xl font-bold text-gray-700 opacity-20">
                                            {services[activeService].title.split(" ")[0]}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </section>

            {/* Process Section */}
            <section className="py-20">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl font-bold text-gray-800 mb-3">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                        Design Process
                    </span></h2>
                    <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                        A structured approach to delivering exceptional results
                    </p>
                </motion.div>


                <div className="relative max-w-4xl mx-auto">
                    <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500 transform -translate-x-1/2" />
                    {processTimeline.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.15 }}
                            viewport={{ once: true }}
                            className={`mb-12 flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-start`}
                        >
                            {/* <div className="hidden lg:block absolute left-1/2 w-4 h-4 rounded-full bg-white border-4 border-blue-500 transform -translate-x-1/2 -translate-y-1/2 z-10" /> */}

                            <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                                <div className="inline-flex items-center mb-4">
                                    <div className={`bg-gradient-to-r ${index % 2 === 0 ? 'from-blue-600 to-purple-600' : 'from-purple-600 to-blue-600'
                                        } w-14 h-14 rounded-full flex items-center justify-center mr-4 shadow-lg`}>
                                        {item.icon}
                                    </div>
                                    <span className="font-bold text-gray-800 text-lg bg-white py-1 px-3 rounded-full border border-gray-200 shadow-sm">{item.year}</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{item.event}</h3>
                                <p className="text-gray-600">{item.description}</p>
                            </div>

                            <div className="w-1/2 flex justify-center">
                                <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full border-4 border-white shadow-xl mt-4" />
                            </div>

                        </motion.div>
                    ))}

                </div>
            </section>



            {/* FAQ Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
                        <p className="text-gray-600">Everything you need to know about our UI/UX design services</p>
                    </div>

                    <div className="space-y-6">
                        {faqItems.map((faq, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
                            >
                                <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                                <p className="text-gray-600">{faq.answer}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default UIDesignPage;